// /**
//  * stat-card.tsx — KPI tile used across dashboard + analytics pages.
//  */
// import type { ReactNode } from "react";
// import { Card, CardContent } from "./card";
// import { cn } from "@/lib/utils";

// export function StatCard({
//   label,
//   value,
//   delta,
//   icon,
//   className,
// }: {
//   label: string;
//   value: ReactNode;
//   delta?: { value: string; positive?: boolean };
//   icon?: ReactNode;
//   className?: string;
// }) {
//   return (
//     <Card className={cn(className)}>
//       <CardContent className="p-5">
//         <div className="flex items-center justify-between">
//           <p className="text-sm font-medium text-muted-foreground">{label}</p>
//           {icon && <div className="text-muted-foreground">{icon}</div>}
//         </div>
//         <p className="mt-2 text-2xl font-bold">{value}</p>
//         {delta && (
//           <p
//             className={cn(
//               "mt-1 text-xs font-medium",
//               delta.positive ? "text-emerald-600" : "text-red-600",
//             )}
//           >
//             {delta.positive ? "▲" : "▼"} {delta.value}
//           </p>
//         )}
//       </CardContent>
//     </Card>
//   );
// }

/**
 * stat-card.tsx — KPI tile used across dashboard + analytics pages.
 *
 * UI POLISH additions (fully backward-compatible — all new props optional):
 *   • `scored`    — when true, colors the value text using scoreColor().
 *   • `scoreMax`  — the max for the score scale (default 100). Only used
 *                   when `scored` is true.
 *
 * Existing callers that don't pass `scored` receive identical rendering to
 * before. No existing prop removed or renamed.
 *
 * Example — scoring on a 10-point scale:
 *   <StatCard label="Opportunity Score" value="8.2" scored scoreMax={10} />
 *
 * Example — existing usage unchanged:
 *   <StatCard label="Emails sent" value="142" icon={<Send />} />
 */
import type { ReactNode } from "react";
import { Card, CardContent } from "./card";
import { cn } from "@/lib/utils";
import { scoreColor } from "@/lib/score-color";

export function StatCard({
  label,
  value,
  delta,
  icon,
  className,
  scored,
  scoreMax = 100,
}: {
  label: string;
  value: ReactNode;
  delta?: { value: string; positive?: boolean };
  icon?: ReactNode;
  className?: string;
  /** When true, the value text is colored green/amber/red by scoreColor(). */
  scored?: boolean;
  /** The maximum value for the score scale. Only used when scored=true. Default 100. */
  scoreMax?: number;
}) {
  // Derive the numeric value for color calculation when scored=true.
  // If value is not a plain number string we skip coloring gracefully.
  const numericValue =
    scored && typeof value === "string"
      ? parseFloat(value.replace(/[^0-9.-]/g, ""))
      : scored && typeof value === "number"
        ? value
        : NaN;

  const valueColorClass =
    scored && !isNaN(numericValue)
      ? scoreColor(numericValue, scoreMax)
      : undefined;

  return (
    <Card className={cn(className)}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-muted-foreground">{label}</p>
          {icon && <div className="text-muted-foreground">{icon}</div>}
        </div>
        <p className={cn("mt-2 text-2xl font-bold", valueColorClass)}>{value}</p>
        {delta && (
          <p
            className={cn(
              "mt-1 text-xs font-medium",
              delta.positive ? "text-emerald-600" : "text-red-600",
            )}
          >
            {delta.positive ? "▲" : "▼"} {delta.value}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
